/**
 * Helper de utilidades para operaciones puras de empleados en RegistrarDatos.
 * Funciones sin dependencia de Vue, siguiendo Clean Code y bajo acoplamiento.
 */
import moment from 'moment';

export const TIPO_CONTRATO = {
  TERMINO_FIJO: 2,
  OPS: 3,
};

const TIPOS_CONTRATO_TEMPORAL = [TIPO_CONTRATO.TERMINO_FIJO, TIPO_CONTRATO.OPS];

const FILTROS_ESTADO_CARTERA = {
  BLOQUEADO: empleado => empleado.Bloqueado,
  DESBLOQUEADO: empleado => !empleado.Bloqueado,
};

const FILTROS_ESTADO_EMPLEADO = {
  DISPONIBLE: empleado => empleado.Disponible,
  'NO DISPONIBLE': empleado => !empleado.Disponible,
};

/**
 * Construye las fechas de un empleado a partir de los datos del formulario o API.
 * @param {Object} fuente - Objeto con FechaIngreso y FechaContratoHasta.
 * @returns {{ fechaIngreso: moment.Moment, fechaContratoHasta: moment.Moment|null }}
 */
const construirFechasEmpleado = fuente => ({
  fechaIngreso: moment.utc(fuente.FechaIngreso || fuente.fechaIngreso),
  fechaContratoHasta: (fuente.FechaContratoHasta || fuente.fechaContratoHasta)
    ? moment.utc(fuente.FechaContratoHasta || fuente.fechaContratoHasta)
    : null,
});

/**
 * Aplica filtros locales sobre el listado de empleados.
 * @param {Array<Object>} empleados - Lista original de empleados.
 * @param {Object} filtros - Objeto con filtros activos.
 * @param {Object|null} filtros.tipoContrato
 * @param {Object|null} filtros.sucursal
 * @param {string|null} filtros.estadoCartera
 * @param {string|null} filtros.estadoEmpleado
 * @returns {Array<Object>} Empleados filtrados.
 */
export const aplicarFiltros = (empleados, filtros) => {
  const filtroTipoContrato = filtros.tipoContrato
    ? empleado => empleado.TipoContrato._id === filtros.tipoContrato._id
    : null;

  const filtroSucursal = filtros.sucursal
    ? empleado => empleado.IdSucursal === filtros.sucursal.sucursal_id
    : null;

  const filtroEstadoCartera = FILTROS_ESTADO_CARTERA[filtros.estadoCartera];
  const filtroEstadoEmpleado = FILTROS_ESTADO_EMPLEADO[filtros.estadoEmpleado];

  const filtrosAplicar = [
    filtroTipoContrato,
    filtroSucursal,
    filtroEstadoCartera,
    filtroEstadoEmpleado,
  ].filter(Boolean);

  return filtrosAplicar.reduce(
    (lista, filtro) => lista.filter(filtro),
    empleados
  );
};

/**
 * Retorna la clase de color Vuetify según el estado de bloqueo.
 * @param {boolean} bloqueado
 * @returns {string} Clase CSS 'error--text' o cadena vacía.
 */
export const getColor = bloqueado => (bloqueado ? 'error--text' : '');

/**
 * Calcula el flag disponible al momento de guardar un empleado.
 * Usa reglas fijas: 6 meses de antigüedad, 1 mes para término fijo.
 * @param {Object} formData - Datos del formulario emitido por el modal.
 * @returns {boolean}
 */
export const calcularDisponibleInicial = formData => {
  const { fechaIngreso, fechaContratoHasta } =
    construirFechasEmpleado(formData);

  if (
    fechaIngreso
      .clone()
      .add(1, 'days')
      .isAfter(moment.utc().subtract(6, 'months'))
  ) {
    return false;
  }

  if (
    fechaContratoHasta &&
    TIPOS_CONTRATO_TEMPORAL.includes(formData.tipoContrato) &&
    fechaContratoHasta.isBefore(moment.utc().add(1, 'month'))
  ) {
    return false;
  }

  return true;
};

/**
 * Construye la base del payload para crear o actualizar un empleado.
 * @param {Object} formData - Datos del formulario emitido por el modal.
 * @param {boolean} disponible - Flag de disponibilidad calculado.
 * @returns {Object}
 */
const construirPayloadBase = (formData, disponible) => ({
  nombres: formData.nombres,
  primerApellido: formData.primerApellido,
  segundoApellido: formData.segundoApellido || null,
  TipoContrato: formData.tipoContrato,
  Cargo: formData.cargo,
  Salario: formData.salario,
  Telefono: formData.telefono,
  FechaIngreso: formData.fechaIngreso,
  NitCompañia: formData.nitCompañia,
  DescCompañia: formData.descCompañia,
  IdSucursal: formData.idSucursal,
  DescripcionSucursal: formData.descripcionSucursal,
  Bloqueado: formData.bloqueado,
  Disponible: disponible,
  Embargo: formData.embargo,
});

/**
 * Construye el payload para crear un empleado.
 * @param {Object} formData
 * @param {boolean} disponible
 * @returns {Object}
 */
/**
 * Agrega FechaContratoHasta al payload cuando el contrato es temporal o la fecha existe.
 * @param {Object} payload - Payload base del empleado.
 * @param {Object} formData - Datos del formulario emitido por el modal.
 * @returns {Object} Payload con la fecha de contrato aplicada.
 */
const aplicarFechaContratoHasta = (payload, formData) => {
  if (
    TIPOS_CONTRATO_TEMPORAL.includes(formData.tipoContrato) ||
    formData.fechaContratoHasta
  ) {
    payload.FechaContratoHasta = formData.fechaContratoHasta;
  }
  return payload;
};

const construirPayloadCreacion = (formData, disponible) => {
  const payload = {
    ...construirPayloadBase(formData, disponible),
    TipoDocumento: formData.tipoDocumento,
    NumeroDocumento: formData.numeroDocumento,
  };

  return aplicarFechaContratoHasta(payload, formData);
};

/**
 * Construye el payload para actualizar un empleado.
 * @param {Object} formData
 * @param {boolean} disponible
 * @returns {Object}
 */
const construirPayloadActualizacion = (formData, disponible) => {
  const payload = {
    ...construirPayloadBase(formData, disponible),
    estaRetirado: formData.estaRetirado,
  };

  return aplicarFechaContratoHasta(payload, formData);
};

/**
 * Construye el objeto que el modal emite al guardar un empleado.
 * Normaliza el formData del formulario al contrato esperado por el Index
 * (campos con nitCompañia, descCompañia, numeroDocumento en string, etc.).
 * @param {Object} formData - Estado del formulario del modal.
 * @param {boolean} esEdicion - Indica si es actualización o registro.
 * @returns {Object} Datos listos para emitir en el evento guardar.
 */
export const construirDatosEmisionEmpleado = (formData, esEdicion) => ({
  _id: formData._id,
  esEdicion,
  tipoEvento: esEdicion ? 'MODIFICAR' : 'REGISTRAR',
  descripcionTipo: esEdicion ? 'MODIFICAR EMPLEADO' : 'REGISTRA EMPLEADO',
  tipoDocumento: formData.tipoDocumento,
  numeroDocumento: formData.numeroDocumento,
  nombres: formData.nombres,
  primerApellido: formData.primerApellido,
  segundoApellido: formData.segundoApellido,
  tipoContrato: formData.tipoContrato,
  cargo: formData.cargo.toUpperCase(),
  salario: Number.parseFloat(formData.salario),
  telefono: formData.telefono,
  fechaIngreso: formData.fechaIngreso,
  fechaContratoHasta: formData.fechaContratoHasta || null,
  nitCompañia: String(formData.compania.nit),
  descCompañia: formData.compania.razon_social,
  idSucursal: formData.sucursal.IdSucursal,
  descripcionSucursal: formData.sucursal.DescripcionSucursal,
  bloqueado: formData.bloqueado,
  disponible: formData.disponible,
  embargo: formData.embargo,
  estaRetirado: formData.estaRetirado,
});

/**
 * Construye el payload para crear o actualizar un empleado en la API.
 * @param {Object} formData - Datos del formulario emitido por el modal.
 * @param {boolean} disponible - Flag de disponibilidad calculado.
 * @returns {Object} Payload listo para enviar a empleadosService.
 */
export const construirPayloadEmpleado = (formData, disponible) => {
  return formData.esEdicion
    ? construirPayloadActualizacion(formData, disponible)
    : construirPayloadCreacion(formData, disponible);
};

/**
 * Convierte un saldo (número o string es-CO con puntos de miles y coma decimal)
 * a un número con decimales.
 * @param {string|number|null|undefined} saldo - Valor tal como lo devuelve la API.
 * @returns {number} Número parseado o 0 si es inválido/vacío.
 */
const parsearSaldo = saldo => {
  if (saldo === null || saldo === undefined || saldo === '') return 0;
  if (typeof saldo === 'number') return Number.isFinite(saldo) ? saldo : 0;

  const normalizado = String(saldo)
    .replace(/[^\d,]/g, '') // quita $, espacios y puntos de miles
    .replace(',', '.'); // coma decimal → punto
  return Number(normalizado) || 0;
};

/**
 * Suma los saldos de una lista de cuentas por cobrar.
 * @param {Array<Object>} cuentas - Lista de cuentas con propiedad Saldo.
 * @returns {number} Suma total de saldos.
 */
export const sumarSaldosCxC = cuentas => {
  if (!cuentas?.length) return 0;
  return cuentas.reduce(
    (total, registro) => total + parsearSaldo(registro.Saldo),
    0
  );
};

/**
 * Filtra empleados cuyas empresas estén actualizadas.
 * @param {Array<Object>} empleados - Lista de empleados.
 * @param {Map<number, Object>} mapaEmpresas - Mapa de nit → empresa.
 * @param {Function} validarActualizacion - Función que recibe una empresa y retorna boolean.
 * @returns {Array<Object>} Empleados de empresas actualizadas.
 */
export const filtrarEmpleadosPorEmpresaActualizada = (
  empleados,
  mapaEmpresas,
  validarActualizacion
) =>
  empleados.filter(empleado => {
    const nit = Number.parseInt(empleado.NitCompañia, 10);
    if (Number.isNaN(nit)) return false;
    const empresa = mapaEmpresas.get(nit);
    if (!empresa) return false;
    return validarActualizacion(empresa);
  });
